import {
    Plugin,
    Widget,
    toWidget,
    viewToModelPositionOutsideModelElement,
    EmitterMixin,
    mix
} from 'ckeditor5';

import DoubleClickObserver from '../observer/doubleclickobserver';
import PlaceholderCommand from './placeholdercommand';

export default class PlaceholderEditing extends Plugin {
    static get requires() {
        return [Widget];
    }

    init() {
        console.log('PlaceholderEditing#init() got called');

        this._defineSchema();
        this._defineConverters();
        this._defineClickObserver();

        this.editor.commands.add('placeholder', new PlaceholderCommand(this.editor));
        this.editor.editing.mapper.on(
            'viewToModelPosition',
            viewToModelPositionOutsideModelElement(this.editor.model, viewElement => viewElement.hasClass('placeholder'))
        );
        this.editor.config.define('placeholderConfig', {
            types: ['date', 'first name', 'surname']
        });
    }

    _defineSchema() {
        const schema = this.editor.model.schema;

        schema.register('placeholder', {
            // Allow wherever text is allowed:
            allowWhere: '$text',

            // The placeholder will act as an inline node:
            isInline: true,

            // The inline widget is self-contained so it cannot be split by the caret and can be selected:
            isObject: true,

            // The inline widget can have the same attributes as text (for example linkHref, bold).
            allowAttributesOf: '$text',

            // The placeholder can have many types, like date, name, surname, etc:
            allowAttributes: ['name', 'dataId', 'dataType', 'dataStatus']
        });
    }

    _defineConverters() {
        const conversion = this.editor.conversion;

        conversion.for('upcast').elementToElement({
            view: {
                name: 'span',
                classes: ['placeholder']
            },
            model: (viewElement, {
                writer: modelWriter
            }) => {
                // Extract the "name" from "{name}".
                const name = viewElement.getChild(0).data.replace(/^\{|\}$/g, '');
                const dataId = viewElement.getAttribute('data-id');
                const dataType = viewElement.getAttribute('data-type');
                const dataStatus = viewElement.getAttribute('data-status');

                return modelWriter.createElement('placeholder', {
                    name,
                    dataId,
                    dataType,
                    dataStatus
                });
            }
        });

        conversion.for('editingDowncast').elementToElement({
            model: 'placeholder',
            view: (modelItem, {
                writer: viewWriter
            }) => {
                const widgetElement = createPlaceholderView(modelItem, viewWriter);

                // Enable widget handling on a placeholder element inside the editing view.
                return toWidget(widgetElement, viewWriter);
            }
        });

        conversion.for('dataDowncast').elementToElement({
            model: 'placeholder',
            view: (modelItem, {
                writer: viewWriter
            }) => createPlaceholderView(modelItem, viewWriter)
        });

        // Helper method for both downcast converters.
        function createPlaceholderView(modelItem, viewWriter) {
            const name = modelItem.getAttribute('name');
            const dataId = modelItem.getAttribute('dataId');
            const dataType = modelItem.getAttribute('dataType');
            const dataStatus = modelItem.getAttribute('dataStatus') || 'N';

            const placeholderView = viewWriter.createContainerElement('span', {
                class: 'placeholder',
                'data-id': dataId,
                'data-type': dataType,
                'data-status': dataStatus
            }, {
                isAllowedInsideAttributeElement: true
            });

            // Insert the placeholder name (as a text).
            const innerText = viewWriter.createText(dataStatus === 'Y' ? name : '{' + name + '}');
            viewWriter.insert(viewWriter.createPositionAt(placeholderView, 0), innerText);

            return placeholderView;
        }
    }

    _defineClickObserver() {
        const view = this.editor.editing.view;
        const viewDocument = view.document;

        if (!view.getObserver(DoubleClickObserver)) {
            view.addObserver(DoubleClickObserver);
        }

        this.editor.listenTo(viewDocument, 'dblclick', (evt, data) => {
            const modelElement = this.editor.editing.mapper.toModelElement(data.target);
            if (modelElement && modelElement.name == 'placeholder') {
                // mark model element as selected
                this.editor.model.change(writer => {
                    writer.setSelection(modelElement, 'on');
                });
                // fire 'change:data' event to angular
                this.delegate('change:data').to(this.editor.model.document);
                this.fire('change:data', modelElement);
            }
        });
    }
}

mix(PlaceholderEditing, EmitterMixin);
